/**
 * Générateur — Automatisme : prendre l'opposé d'une expression :
 * −(5 − 4x) = −5 + 4x.
 * Attendu ID : 1233 | Niveau : 3ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 11, ligne 800 :
 * « Prendre l'opposé d'une expression : savoir que −(5 − 4x) = −5 + 4x. »,
 * la base du prof omet « savoir que »).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * L'opposé d'un BINÔME, l'ordre des termes CONSERVÉ (−(5 − 4x) = −5 + 4x,
 * l'exemple du programme) : chaque terme change de signe. Le mapping
 * prévoyait une extraction de gen_208 (3e objectif) ; ses cartes forcent
 * l'ordre ax + b et portent une consigne d'observation : ici un gen neuf
 * compact aux mêmes règles (écart au mapping, À ARBITRER). Formes = bornes :
 * b − ax, ax − b, ax + b, b + ax, −ax + b (a, b 1..9) ; le trinôme, la
 * suppression de parenthèses dans une somme (objectif 4e, cartes
 * parentheses / crochets de 208) et le développement par −k sont exclus.
 * Banque MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 3ND3
 * option 0 « Opposé d'une expression » (deux coefficients −9..9).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Écris l'opposé » (oppose_saisie) : « Écris l'opposé de
 *     8 − 2x » en SAISIE LIBRE, comparaison d'expression (décision prof
 *     13/09) : « -8 + 2x », « 2x - 8 », « +2x - 8 », « 2*x - 8 » admis, les
 *     espaces et le signe moins typographique indifférents ; le corrigé
 *     affiche l'opposé terme à terme.
 *   Carte 2 « L'opposé » (oppose_qcm_unique) : « L'opposé de 5 − 4x
 *     est… » [−5 + 4x ; −5 − 4x ; 5 + 4x ; −x] ; 4x − 5 n'est JAMAIS un
 *     distracteur (même polynôme que la clé).
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : quatre lignes, dont la
 *     fondation « pour x = −3, −x = 3 » (V) et « −x est toujours négatif »
 *     (F : DEPP 2de 2025 q.9, réussite 38,0 %).
 *
 * INVARIANTS : l'opposé re-vérifié terme à terme (somme des deux
 * expressions = 0 comme polynôme, throw) ; distracteurs distincts de la clé
 * comme polynômes.
 * SACRIFIÉ (fiche « L'opposé de chaque terme ») : (5 ; −4) = 5 − 4x, toute
 * lettre ; auto-testé par gen1233.self.test.js.
 *
 * ── Erreurs (familles nommées ; DEPP 2de 2025 q.9 « −x », 38,0 %) ────────
 *   un_seul_terme        : −5 − 4x (seul le premier terme changé), PRINCIPALE ;
 *   dernier_seul         : 5 + 4x (seul le second changé) ;
 *   moins_avale          : 5 − 4x (rien ne change : le moins avalé) ;
 *   termes_non_semblables : −x (5 − 4x réduit à x, puis opposé) ;
 *   moins_donc_negatif   : « −x est toujours négatif » (Vrai/Faux).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie, tex, ecrituresPolynome,
} from '../../../utils';

const ATTENDU_ID = 1233;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_SAISIE = 'Écris l\'opposé';
const L_QCM = 'L\'opposé';
const L_VF = 'Vrai ou faux';

const LETTRES = ['x', 'x', 'x', 'n', 't', 'y'];

// ─── Le domaine : un binôme = deux termes ordonnés { coef, deg } ──────────

/** L'exemple du programme et de la fiche : 5 − 4x, toute lettre. */
export const SACRIFIE = [{ coef: 5, deg: 0 }, { coef: -4, deg: 1 }];
const signature = (t) => t.map((u) => `${u.coef}d${u.deg}`).join(',');
const estSacrifie = (termes) => signature(termes) === signature(SACRIFIE);

/** Un binôme parmi les cinq formes ; `avecMoins` impose un signe moins dans l'écriture. */
const tirer = ({ exclure = [] } = {}) => {
    for (let essai = 0; essai < 200; essai++) {
        const a = randInt(1, 9); const b = randInt(1, 9);
        const forme = randomChoice(['b_moins_ax', 'b_moins_ax', 'ax_moins_b', 'ax_plus_b', 'b_plus_ax', 'moins_ax_plus_b']);
        const termes = {
            b_moins_ax: [{ coef: b, deg: 0 }, { coef: -a, deg: 1 }],
            ax_moins_b: [{ coef: a, deg: 1 }, { coef: -b, deg: 0 }],
            ax_plus_b: [{ coef: a, deg: 1 }, { coef: b, deg: 0 }],
            b_plus_ax: [{ coef: b, deg: 0 }, { coef: a, deg: 1 }],
            moins_ax_plus_b: [{ coef: -a, deg: 1 }, { coef: b, deg: 0 }],
        }[forme];
        if (estSacrifie(termes) || exclure.includes(signature(termes))) continue;
        const oppose = termes.map((t) => ({ coef: -t.coef, deg: t.deg }));
        // assert : terme à terme, la somme est nulle
        termes.forEach((t, i) => { if (t.coef + oppose[i].coef !== 0 || t.deg !== oppose[i].deg) throw new Error('gen_1233 : opposé'); });
        return { forme, termes, oppose };
    }
    return { forme: 'ax_moins_b', termes: [{ coef: 3, deg: 1 }, { coef: -7, deg: 0 }], oppose: [{ coef: -3, deg: 1 }, { coef: 7, deg: 0 }] };
};

// ─── Écritures (KaTeX) ────────────────────────────────────────────────────

const mono = ({ coef, deg }, L) => `${Math.abs(coef) === 1 && deg === 1 ? '' : Math.abs(coef)}${deg === 1 ? L : ''}`;
/** « 5 - 4x », « -4x + 5 » : le premier signe collé, le second espacé. */
const expr = (termes, L) => termes.map((t, i) => (i === 0 ? `${t.coef < 0 ? '-' : ''}${mono(t, L)}` : ` ${t.coef < 0 ? '-' : '+'} ${mono(t, L)}`)).join('');

const EXPLICATIONS = {
    un_seul_terme: 'L\'opposé change le signe de CHAQUE terme : −(5 − 4x) = −5 + 4x. Le −4x devient +4x.',
    dernier_seul: 'Le premier terme change aussi : −(5 − 4x) = −5 + 4x, pas 5 + 4x.',
    moins_avale: 'Le moins devant la parenthèse agit : il change les deux signes.',
    termes_non_semblables: '5 et 4x ne se regroupent pas : l\'opposé de 5 − 4x garde deux termes, −5 + 4x.',
    moins_donc_negatif: '−x n\'est pas toujours négatif : pour x = −3, −x = 3.',
    regle: 'L\'opposé d\'une expression : chaque terme change de signe, l\'ordre ne change pas.',
};

// ─── Carte 1 : Écris l'opposé (saisie libre, comparaison d'expression) ───

/**
 * L'élève tape l'opposé lui-même (« -8 + 2x », « 2x - 8 », « +2x - 8 » : tous
 * les ordres sont admis via contenu.reponses_acceptees, décision prof 13/09).
 * bonne_reponse = l'opposé terme à terme dans l'ordre de l'écriture (le corrigé).
 */
const genSaisie = () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randomChoice(LETTRES);
        const it = tirer();
        const ecritures = ecrituresPolynome(it.oppose, L);
        const payload = makeSaisie(ecritures[0], `<p><b>Écris l'opposé de ${tex(expr(it.termes, L))}</b> (sans parenthèses).</p>`, {
            ...opts(L_SAISIE), strict: true, comparaison: 'expression', reponses_acceptees: ecritures,
        });
        payload.contenu = { ...payload.contenu, sousType: it.forme, explications: { regle: EXPLICATIONS.regle, un_seul_terme: EXPLICATIONS.un_seul_terme } };
        payload._meta.params = { ...it, lettre: L };
        return payload;
    }
    throw new Error('gen_1233 : saisie sans tirage');
};

// ─── Carte 2 : L'opposé (QCM) ─────────────────────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randomChoice(LETTRES);
        const it = tirer();
        const [t1, t2] = it.termes;
        const bon = tex(expr(it.oppose, L));
        const faux = [
            { code: 'un_seul_terme', termes: [{ coef: -t1.coef, deg: t1.deg }, t2] },
            { code: 'dernier_seul', termes: [t1, { coef: -t2.coef, deg: t2.deg }] },
            { code: 'moins_avale', termes: [t1, t2] },
            { code: 'termes_non_semblables', termes: [{ coef: -(t1.coef + t2.coef) || 1, deg: 1 }] },
        ].map((f) => ({ ...f, texte: tex(expr(f.termes, L)) })).filter((f) => f.texte !== bon && !estSacrifie(f.termes)); // 5 − 4x jamais, même en distracteur
        const pris = shuffle(faux).slice(0, 3);
        const choix = [bon, ...pris.map((f) => f.texte)];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, bon, `<p><b>L'opposé de ${tex(expr(it.termes, L))} est…</b></p>`, opts(L_QCM));
        payload.contenu = { ...payload.contenu, sousType: it.forme, explications: EXPLICATIONS };
        payload._meta.params = { ...it, lettre: L, distracteurs: pris.map(({ code }) => ({ code })) };
        return payload;
    }
    const bon = tex('-3x + 7');
    const payload = makeQcm([bon, tex('-3x - 7'), tex('3x + 7'), tex('3x - 7')], bon, `<p><b>L'opposé de ${tex('3x - 7')} est…</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, sousType: 'ax_moins_b', explications: EXPLICATIONS };
    payload._meta.params = { forme: 'ax_moins_b', termes: [{ coef: 3, deg: 1 }, { coef: -7, deg: 0 }], oppose: [{ coef: -3, deg: 1 }, { coef: 7, deg: 0 }], lettre: 'x', distracteurs: [{ code: 'un_seul_terme' }, { code: 'dernier_seul' }, { code: 'moins_avale' }] };
    return payload;
};

// ─── Carte 3 : Vrai ou faux ───────────────────────────────────────────────

const genVf = () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randomChoice(LETTRES);
        const items = [tirer(), tirer()];
        const verites = shuffle([true, false]);
        const lignes = items.map((it, i) => {
            if (verites[i]) return { ligne: tex(`-(${expr(it.termes, L)}) = ${expr(it.oppose, L)}`), code: null };
            const [t1, t2] = it.termes;
            const f = randomChoice([{ code: 'un_seul_terme', termes: [{ coef: -t1.coef, deg: t1.deg }, t2] }, { code: 'dernier_seul', termes: [t1, { coef: -t2.coef, deg: t2.deg }] }].filter((g) => !estSacrifie(g.termes)));
            return { ligne: tex(`-(${expr(it.termes, L)}) = ${expr(f.termes, L)}`), code: f.code };
        });
        // la fondation : −x pour une valeur, et « −x est toujours négatif »
        const v = randInt(2, 9);
        const fondation = randomChoice([
            { ligne: `Pour ${tex(`${L} = -${v}`)}, ${tex(`-${L} = ${v}`)}.`, vraie: true, code: null },
            { ligne: `Pour ${tex(`${L} = -${v}`)}, ${tex(`-${L} = -${v}`)}.`, vraie: false, code: 'moins_donc_negatif' },
            { ligne: `Pour ${tex(`${L} = ${v}`)}, ${tex(`-${L} = -${v}`)}.`, vraie: true, code: null },
        ]);
        const toujours = { ligne: `${tex(`-${L}`)} est toujours un nombre négatif.`, vraie: false, code: 'moins_donc_negatif' };
        const toutes = shuffle([...lignes.map((l, i) => ({ ...l, vraie: verites[i] })), fondation, toujours]);
        if (new Set(toutes.map((l) => l.ligne)).size < 4) continue;
        const payload = makeVraiFaux(toutes.map((l) => l.ligne), toutes.map((l) => (l.vraie ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, lettre: L, verites: toutes.map((l) => l.vraie), distracteurs: toutes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    const lignes = [tex('-(3x - 7) = -3x + 7'), tex('-(2 + 5x) = -2 + 5x'), `Pour ${tex('x = -3')}, ${tex('-x = 3')}.`, `${tex('-x')} est toujours un nombre négatif.`];
    const payload = makeVraiFaux(lignes, [0, 1, 0, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: [], lettre: 'x', verites: [true, false, true, false], distracteurs: [{ code: 'dernier_seul' }, { code: 'moins_donc_negatif' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'binômes b − ax, ax − b, ax + b, b + ax, −ax + b (a, b 1..9), ordre conservé ; sacrifié 5 − 4x toute lettre',
        'quatre écritures distinctes ; 4x − 5 (même polynôme que −5 + 4x) n\'est jamais un distracteur',
    ],
    oppose_qcm_unique: 'un_seul_terme (principale), dernier_seul, moins_avale, termes_non_semblables',
    vf_tableau_vf: 'deux opposés (un vrai, un faux), une ligne de fondation « pour x = −3, −x = 3 », et « −x est toujours négatif » (F, DEPP 2de 2025 q.9)',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    oppose_saisie: genSaisie,
    oppose_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'ecrire', label: L_SAISIE, etapes: 1, formatsList: [{ key: 'oppose_saisie', label: 'Saisie' }] },
    { id: 'oppose', label: L_QCM, etapes: 1, formatsList: [{ key: 'oppose_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, etapes: 1, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
